import { useEffect, useState } from "react";

import { DropDown } from "@assets";
import { Button } from "@components/Button";
import { ButtonSize } from "@components/Button/types";
import { ErrorComponent } from "@components/ErrorComponent";
import { Row } from "@components/Flex";
import { Loader } from "@components/Loader";
import { MainLayout, MainLayoutProps } from "@components/MainLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { Body } from "@components/Typography";
import {
  useAllocateFundsFlow,
  useAllocatePromotionalDollars,
  useErrorBanner,
  useModal,
  usePromotionalDollarQuery,
  useUser,
} from "@hooks";
import { Translations, i18n } from "@i18n";
import { convertDollarToCents, formatCurrencyInput, isValidDecimalInput } from "@utils";
import { AllocateFundsSuccessModal } from "./AllocateFundsSuccessModal";
import { SelectBeneficiaryModal } from "./SelectBeneficiaryModal";
import { SelectGoalModal } from "./SelectGoalModal";
import { StyledInput, useStyles } from "./styles";
const MINIMUM_AMOUNT = 1;

export const AllocateFundsRoute = () => {
  const styles = useStyles();

  const [value, setValue] = useState("");

  const { show } = useModal();

  const { beneficiaries, loading: userLoading, error: userError } = useUser();

  const {
    allocateFundsProps: { goalId, beneficiaryId },
    setAllocateFundsProps,
    clearAllocateFundsProps,
  } = useAllocateFundsFlow();

  const {
    promotionalBalance,
    loading: promotionalBalanceLoading,
    error: promotionalBalanceError,
  } = usePromotionalDollarQuery();

  const [allocateFunds, { loading: isAllocatingFunds }] = useAllocatePromotionalDollars();

  useEffect(() => {
    clearAllocateFundsProps();
  }, []);

  const { showErrorBanner } = useErrorBanner();

  const loading = userLoading || promotionalBalanceLoading;
  const error = userError || promotionalBalanceError;

  const beneficiary = beneficiaries?.find((b) => b?.id === beneficiaryId);
  const beneficiaryName =
    beneficiary?.firstName ??
    (i18n.t(Translations.AllocateFundsRouteBeneficiaryPlaceholder) as string);

  const goal = beneficiary?.goals?.find((g) => g?.id === goalId);
  const goalName = goal?.name ?? (i18n.t(Translations.AllocateFundsRouteGoalPlaceholder) as string);

  const amount = Number(value.replace(/[^0-9.]/g, ""));

  const isBelowMinAmount = amount < MINIMUM_AMOUNT && value.length > 0;
  const isAboveMaxAmount = amount > promotionalBalance;
  const isInvalidInput = !isValidDecimalInput(value);

  const inputError = (() => {
    if (isBelowMinAmount) {
      return i18n.t(Translations.AllocateFundsRouteAmountInputMinAmountError, {
        amount: formatCurrencyInput({
          input: MINIMUM_AMOUNT.toFixed(2),
          decimals: true,
        }),
      });
    }

    if (isAboveMaxAmount) {
      return i18n.t(Translations.AllocateFundsRouteAmountInputMaxAmountError, {
        amount: formatCurrencyInput({
          input: promotionalBalance.toFixed(2),
          decimals: true,
        }),
      });
    }

    if (isInvalidInput) return i18n.t(Translations.AllocateFundsRouteAmountInputInvalidFormatError);

    return undefined;
  })();

  const disabled = Boolean(inputError || value.length === 0 || !beneficiaryId || !goalId);

  const props: MainLayoutProps = {
    pageHeader: i18n.t(Translations.AllocateFundsRouteHeader),
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = e.target.value.replace(/[^0-9.]/g, "");

    const parts = inputValue.split(".");

    if (parts.length > 2) {
      inputValue = parts[0] + "." + parts.slice(1).join("");
    }
    if (parts[1]?.length > 2) {
      inputValue = parts[0] + "." + parts[1].slice(0, 2);
    }

    setValue(inputValue);
  };

  if (loading) return <Loader />;
  if (error) return <ErrorComponent />;

  const handleSubmit = async () => {
    if (!goalId) return showErrorBanner();

    try {
      await allocateFunds({
        variables: {
          destinationGoalId: goalId,
          amountInCents: convertDollarToCents(amount),
        },
      });

      setAllocateFundsProps({
        allocatedAmount: amount,
      });

      show(
        <AllocateFundsSuccessModal
          allocatedAmount={amount}
          remainingAmount={promotionalBalance - amount}
          name={beneficiaryName}
          callback={() => setValue("")}
        />,
        false,
      );
    } catch (e: any) {
      const message = e.graphQLErrors?.[0]?.message;
      showErrorBanner(message);
    }
  };

  return (
    <MainLayout {...props}>
      <div style={styles.container}>
        <Spacing size={SpacingSizes.XL} />
        <StyledInput
          type="text"
          value={value ? `$${value}` : ""}
          onChange={handleChange}
          placeholder="$0"
          style={styles.input}
        />
      </div>
      <Spacing size={SpacingSizes.XS} />

      {inputError && <Body style={styles.error}>{inputError}</Body>}

      <Row style={{ justifyContent: "center", width: "100%" }}>
        <Body>${promotionalBalance ?? 0}</Body>
        <Body style={styles.availableBalance}>
          {i18n.t(Translations.AllocateFundsRouteAvailableBalance)}
        </Body>
      </Row>

      <Spacing size={SpacingSizes.XXL} />

      <Row style={styles.pillOptionsRow}>
        <div style={styles.pillOption} onClick={() => show(<SelectBeneficiaryModal />)}>
          <Body style={styles.pillText}>{beneficiaryName}</Body>
          <span style={styles.pillArrow}>
            <DropDown />
          </span>
        </div>
        <div
          style={{
            ...styles.pillOption,
            ...(!beneficiaryId ? styles.pillOptionDisabled : {}),
          }}
          onClick={() => {
            if (beneficiaryId) show(<SelectGoalModal />);
          }}
          aria-disabled={!beneficiaryId}
        >
          <Body style={styles.pillText}>{goalName}</Body>
          <span style={styles.pillArrow}>
            <DropDown />
          </span>
        </div>
      </Row>
      <Spacing size={SpacingSizes.XXL} />

      <div style={styles.button}>
        <Button
          title={i18n.t(Translations.AllocateFundsRouteAllocatePrimaryCTA)}
          onClick={handleSubmit}
          size={ButtonSize.Small}
          loading={isAllocatingFunds}
          disabled={disabled}
        />
      </div>
    </MainLayout>
  );
};
